import React, { useState, useEffect } from "react";
import { FiSearch, FiX, FiChevronDown, FiFilter } from "react-icons/fi";
import { salesPersonOptionLabel } from "../../../services/salesPersonApi";

const STATUS_OPTIONS = ["Active", "Inactive", "Interested", "Not Interested"];
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];
const LEAD_SOURCE_OPTIONS = [
  "Facebook",
  "WhatsApp",
  "Instagram",
  "LinkedIn",
  "Email",
  "Referral",
  "Direct",
  "Other",
];
const SORT_OPTIONS = [
  { value: "createdAt:desc", label: "Newest first" },
  { value: "createdAt:asc", label: "Oldest first" },
  { value: "updatedAt:desc", label: "Recently updated" },
  { value: "name:asc", label: "Name (A → Z)" },
  { value: "name:desc", label: "Name (Z → A)" },
  { value: "priority:asc", label: "Priority (High → Low)" },
  { value: "status:asc", label: "Status (A → Z)" },
];

const initialFilters = {
  tags: [],
  category: "",
  priority: "",
  status: "",
  leadSource: "",
  assignedTo: "",
  salesPerson: "",
  sortBy: "createdAt",
  sortOrder: "desc",
};

const FilterBar = ({
  searchValue = "",
  onSearch,
  filters: filtersProp,
  onFilter,
  onClearFilters,
  availableTags = [],
  availableCategories = [],
  availableConsultants = [],
  availableSalesPersons = [],
  isAdmin = false,
}) => {
  const [searchQuery, setSearchQuery] = useState(searchValue);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(
    filtersProp || initialFilters,
  );

  useEffect(() => {
    setSearchQuery(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (filtersProp) setActiveFilters(filtersProp);
  }, [filtersProp]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch?.(query);
  };

  const updateFilters = (next) => {
    setActiveFilters(next);
    onFilter?.(next);
  };

  const handleSingleFilter = (key, value) => {
    const next = { ...activeFilters };
    next[key] = next[key] === value ? "" : value;
    updateFilters(next);
  };

  const handleSelectFilter = (key, value) => {
    const next = { ...activeFilters, [key]: value };
    updateFilters(next);
  };

  const handleSortChange = (value) => {
    const [sortBy, sortOrder] = value.split(":");
    const next = { ...activeFilters, sortBy, sortOrder };
    updateFilters(next);
  };

  const handleTagToggle = (tag) => {
    const next = { ...activeFilters };
    next.tags = next.tags.includes(tag)
      ? next.tags.filter((t) => t !== tag)
      : [...next.tags, tag];
    updateFilters(next);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveFilters(initialFilters);
    onClearFilters?.();
    setShowFilters(false);
  };

  const activeFilterCount =
    (activeFilters.tags?.length || 0) +
    (activeFilters.category ? 1 : 0) +
    (activeFilters.priority ? 1 : 0) +
    (activeFilters.status ? 1 : 0) +
    (activeFilters.leadSource ? 1 : 0) +
    (isAdmin && activeFilters.assignedTo ? 1 : 0) +
    (isAdmin && activeFilters.salesPerson ? 1 : 0);

  const hasAny = searchQuery || activeFilterCount > 0;

  const currentSortValue = `${activeFilters.sortBy || "createdAt"}:${
    activeFilters.sortOrder || "desc"
  }`;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 relative min-w-[240px]">
          <FiSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Search leads by name, email, phone or address..."
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-9 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                onSearch?.("");
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <FiX size={16} />
            </button>
          )}
        </div>

        <select
          value={currentSortValue}
          onChange={(e) => handleSortChange(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600"
          title="Sort"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              Sort: {opt.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
            activeFilterCount > 0
              ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          <FiFilter size={16} />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-blue-600 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
          <FiChevronDown
            size={16}
            className={`transition-transform ${showFilters ? "rotate-180" : ""}`}
          />
        </button>

        {hasAny && (
          <button
            type="button"
            onClick={handleClearAll}
            className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md font-medium transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      {showFilters && (
        <div className="border-t border-gray-200 mt-4 pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Priority
            </h4>
            <div className="space-y-1">
              {PRIORITY_OPTIONS.map((priority) => (
                <label
                  key={priority}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={activeFilters.priority === priority}
                    onChange={() => handleSingleFilter("priority", priority)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">{priority}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Status
            </h4>
            <div className="space-y-1">
              {STATUS_OPTIONS.map((status) => (
                <label
                  key={status}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={activeFilters.status === status}
                    onChange={() => handleSingleFilter("status", status)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">{status}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Category
            </h4>
            {availableCategories.length === 0 ? (
              <p className="text-xs text-gray-400">No categories yet</p>
            ) : (
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {availableCategories.map((category) => (
                  <label
                    key={category}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={activeFilters.category === category}
                      onChange={() =>
                        handleSingleFilter("category", category)
                      }
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600 truncate">
                      {category}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Tags
            </h4>
            {availableTags.length === 0 ? (
              <p className="text-xs text-gray-400">No tags yet</p>
            ) : (
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {availableTags.map((tag) => (
                  <label
                    key={tag}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={activeFilters.tags.includes(tag)}
                      onChange={() => handleTagToggle(tag)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600 truncate">
                      {tag}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Lead Source
            </h4>
            <select
              value={activeFilters.leadSource || ""}
              onChange={(e) =>
                handleSelectFilter("leadSource", e.target.value)
              }
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600"
            >
              <option value="">All sources</option>
              {LEAD_SOURCE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Consultant
              </h4>
              <select
                value={activeFilters.assignedTo || ""}
                onChange={(e) =>
                  handleSelectFilter("assignedTo", e.target.value)
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600"
              >
                <option value="">All consultants</option>
                {availableConsultants.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name ||
                      `${c.firstName || ""} ${c.lastName || ""}`.trim() ||
                      c.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isAdmin && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Sales Person
              </h4>
              <select
                value={activeFilters.salesPerson || ""}
                onChange={(e) =>
                  handleSelectFilter("salesPerson", e.target.value)
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-600"
              >
                <option value="">All sales persons</option>
                {availableSalesPersons.map((sp) => (
                  <option key={sp._id || sp.id} value={sp._id || sp.id}>
                    {salesPersonOptionLabel(sp)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {activeFilterCount > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {activeFilters.priority && (
            <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5">
              Priority: {activeFilters.priority}
              <button
                type="button"
                onClick={() =>
                  handleSingleFilter("priority", activeFilters.priority)
                }
                className="hover:text-blue-900"
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {activeFilters.status && (
            <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5">
              Status: {activeFilters.status}
              <button
                type="button"
                onClick={() =>
                  handleSingleFilter("status", activeFilters.status)
                }
                className="hover:text-blue-900"
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {activeFilters.category && (
            <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5">
              Category: {activeFilters.category}
              <button
                type="button"
                onClick={() =>
                  handleSingleFilter("category", activeFilters.category)
                }
                className="hover:text-blue-900"
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {activeFilters.leadSource && (
            <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5">
              Source: {activeFilters.leadSource}
              <button
                type="button"
                onClick={() => handleSelectFilter("leadSource", "")}
                className="hover:text-blue-900"
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {isAdmin && activeFilters.assignedTo && (
            <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5">
              Consultant:{" "}
              {availableConsultants.find(
                (c) => (c._id || c.id) === activeFilters.assignedTo,
              )?.name ||
                availableConsultants.find(
                  (c) => (c._id || c.id) === activeFilters.assignedTo,
                )?.firstName ||
                "Selected"}
              <button
                type="button"
                onClick={() => handleSelectFilter("assignedTo", "")}
                className="hover:text-blue-900"
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {isAdmin && activeFilters.salesPerson && (
            <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5">
              Sales Person:{" "}
              {availableSalesPersons.find(
                (sp) => (sp._id || sp.id) === activeFilters.salesPerson,
              )?.name ||
                availableSalesPersons.find(
                  (sp) => (sp._id || sp.id) === activeFilters.salesPerson,
                )?.email ||
                "Selected"}
              <button
                type="button"
                onClick={() => handleSelectFilter("salesPerson", "")}
                className="hover:text-blue-900"
              >
                <FiX size={12} />
              </button>
            </span>
          )}
          {activeFilters.tags.map((tag) => (
            <span
              key={tag}
              className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5"
            >
              Tag: {tag}
              <button
                type="button"
                onClick={() => handleTagToggle(tag)}
                className="hover:text-blue-900"
              >
                <FiX size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
